import { useState } from "react";
import { motion, AnimatePresence } from "framer-motion";
import { X } from "lucide-react";
import { GALLERY, GALLERY_FILTERS } from "@/lib/data";

const Gallery = () => {
  const [filter, setFilter] = useState("All");
  const [active, setActive] = useState(null);

  const items = GALLERY.filter((g) => filter === "All" || g.category === filter);

  return (
    <section
      id="gallery"
      data-testid="gallery-section"
      className="bg-[#F9F8F6] px-6 md:px-10 lg:px-16 py-24 md:py-36"
    >
      <div className="mx-auto max-w-[1500px]">
        <div className="grid grid-cols-12 gap-6 mb-12 md:mb-16 items-end">
          <div className="col-span-12 md:col-span-7">
            <p className="overline mb-5">Selected Work</p>
            <h2
              data-testid="gallery-heading"
              className="serif text-4xl md:text-5xl lg:text-6xl font-light leading-[1.05] tracking-tight text-[#2A2A2A]"
            >
              The <span className="italic text-[#C48B71]">archive</span>.
            </h2>
          </div>
          <div className="col-span-12 md:col-span-5 flex flex-wrap gap-2 md:justify-end">
            {GALLERY_FILTERS.map((f) => (
              <button
                key={f}
                onClick={() => setFilter(f)}
                data-testid={`gallery-filter-${f.toLowerCase()}`}
                className={`overline px-4 py-2 border transition-all duration-300 ${
                  filter === f
                    ? "bg-[#2A2A2A] text-[#F9F8F6] border-[#2A2A2A]"
                    : "bg-transparent text-[#2A2A2A] border-[#E6E2DA] hover:border-[#2A2A2A]"
                }`}
              >
                {f}
              </button>
            ))}
          </div>
        </div>

        {/* Masonry-ish grid */}
        {items.length === 0 ? (
          <div
            data-testid="gallery-empty"
            className="border border-dashed border-[#E6E2DA] py-24 md:py-32 text-center"
          >
            <p className="serif italic text-3xl md:text-4xl text-[#2A2A2A]">
              Coming soon.
            </p>
            <p className="text-[#5C5C5C] text-sm md:text-base font-light mt-3 max-w-md mx-auto leading-relaxed">
              New <span className="text-[#C48B71] lowercase">{filter}</span> work is being added — check back, or book a session and be the first feature.
            </p>
            <a
              href="#book"
              data-testid="gallery-empty-cta"
              className="btn-primary mt-8 inline-flex"
            >
              Book a session
            </a>
          </div>
        ) : (
          <div className="grid grid-cols-2 md:grid-cols-4 gap-3 md:gap-5 auto-rows-[180px] md:auto-rows-[230px]">
            <AnimatePresence mode="popLayout">
              {items.map((g, i) => (
                <motion.button
                  key={`${g.src}-${i}`}
                  layout
                  initial={{ opacity: 0, scale: 0.96 }}
                  animate={{ opacity: 1, scale: 1 }}
                  exit={{ opacity: 0, scale: 0.96 }}
                  transition={{ duration: 0.5, ease: [0.22, 1, 0.36, 1] }}
                  onClick={() => setActive(g)}
                  data-testid={`gallery-item-${i}`}
                  className={`img-hover relative block overflow-hidden bg-[#E6E2DA] ${
                    g.aspect === "tall" ? "row-span-2" : ""
                  } ${i % 5 === 0 ? "col-span-2 md:col-span-2" : ""}`}
                >
                  <img
                    src={g.src}
                    alt={g.alt}
                    className="absolute inset-0 h-full w-full object-cover"
                    style={{ objectPosition: g.position || "center" }}
                  />
                  <div className="absolute inset-0 bg-gradient-to-t from-black/40 via-transparent to-transparent opacity-0 hover:opacity-100 transition-opacity duration-500 flex items-end p-4">
                    <span className="overline text-[#F9F8F6]">{g.category}</span>
                  </div>
                </motion.button>
              ))}
            </AnimatePresence>
          </div>
        )}
      </div>

      {/* Lightbox */}
      <AnimatePresence>
        {active && (
          <motion.div
            initial={{ opacity: 0 }}
            animate={{ opacity: 1 }}
            exit={{ opacity: 0 }}
            transition={{ duration: 0.3 }}
            data-testid="gallery-lightbox"
            className="fixed inset-0 z-[60] bg-[#2A2A2A]/95 flex items-center justify-center p-6"
            onClick={() => setActive(null)}
          >
            <button
              onClick={() => setActive(null)}
              data-testid="gallery-lightbox-close"
              className="absolute top-6 right-6 text-[#F9F8F6] hover:text-[#C48B71] transition-colors"
              aria-label="Close lightbox"
            >
              <X size={28} strokeWidth={1.2} />
            </button>
            <motion.img
              key={active.src}
              initial={{ scale: 0.96, opacity: 0 }}
              animate={{ scale: 1, opacity: 1 }}
              exit={{ scale: 0.96, opacity: 0 }}
              transition={{ duration: 0.4 }}
              src={active.src}
              alt={active.alt}
              className="max-h-[85vh] max-w-[90vw] object-contain"
              onClick={(e) => e.stopPropagation()}
            />
            <p className="absolute bottom-6 overline text-[#F9F8F6]/80">
              {active.category} — {active.alt}
            </p>
          </motion.div>
        )}
      </AnimatePresence>
    </section>
  );
};

export default Gallery;
